import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { Button, Drawer, ErrorBanner, Field, InputField, Textarea } from '@saas-kit/ui';

import type { CreateInvoicePayload } from './api';

interface Props {
  open: boolean;
  tenantName: string;
  defaultCurrency?: string | null;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateInvoicePayload) => Promise<void>;
}

interface FormState {
  invoice_number: string;
  currency: string;
  subtotal_amount: string;
  tax_amount: string;
  due_at: string;
  notes: string;
}

const EMPTY: FormState = {
  invoice_number: '',
  currency: '',
  subtotal_amount: '',
  tax_amount: '0.00',
  due_at: '',
  notes: '',
};

export function CreateInvoiceDialog({
  open,
  tenantName,
  defaultCurrency,
  isPending,
  onOpenChange,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, currency: (defaultCurrency ?? 'USD').toUpperCase() });
      setError(null);
    }
  }, [open, defaultCurrency]);

  function update<K extends keyof FormState>(key: K, value: string): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    setError(null);
    const payload: CreateInvoicePayload = {
      currency: form.currency.trim().toUpperCase(),
      subtotal_amount: form.subtotal_amount.trim(),
      tax_amount: form.tax_amount.trim() || '0.00',
      invoice_number: form.invoice_number.trim() || null,
      due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
      notes: form.notes.trim() || null,
    };
    void (async () => {
      try {
        await onSubmit(payload);
        onOpenChange(false);
      } catch (err) {
        setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('create.title')}
      description={t('create.description', { tenant: tenantName })}
      testId="invoice-create-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('create.cancel')}
          </Button>
          <Button
            type="submit"
            form="invoice-create-form"
            disabled={isPending}
          >
            {isPending ? t('create.saving') : t('create.submit')}
          </Button>
        </div>
      }
    >
      <form
        id="invoice-create-form"
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <InputField
          label={t('create.invoice_number')}
          name="invoice_number"
          placeholder={t('create.invoice_number_placeholder')}
          value={form.invoice_number}
          onChange={(e) => update('invoice_number', e.target.value)}
          hint={t('create.invoice_number_hint')}
        />
        <InputField
          label={t('create.currency')}
          name="currency"
          required
          maxLength={3}
          value={form.currency}
          onChange={(e) => update('currency', e.target.value.toUpperCase())}
        />
        <div className="grid grid-cols-2 gap-3">
          <InputField
            label={t('create.subtotal')}
            name="subtotal"
            required
            inputMode="decimal"
            placeholder="0.00"
            value={form.subtotal_amount}
            onChange={(e) => update('subtotal_amount', e.target.value)}
          />
          <InputField
            label={t('create.tax')}
            name="tax"
            inputMode="decimal"
            placeholder="0.00"
            value={form.tax_amount}
            onChange={(e) => update('tax_amount', e.target.value)}
          />
        </div>
        <InputField
          label={t('create.due_at')}
          name="due_at"
          type="datetime-local"
          value={form.due_at}
          onChange={(e) => update('due_at', e.target.value)}
        />
        <Field label={t('create.notes')} htmlFor="notes">
          <Textarea
            id="notes"
            rows={3}
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
          />
        </Field>
      </form>
    </Drawer>
  );
}
